import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, TrendingUp, Ship, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const ShipEmissionsArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-red-500 text-white">
                <Globe className="h-3 w-3 mr-1" />
                {t('article.emissions.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.emissions.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.emissions.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.emissions.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/00bd997b-ae7d-4353-b6ac-156ec63c5104.jpg"
              alt="EU shipping emissions data and environmental impact charts" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.emissions.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{t('article.emissions.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.emissions.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Ship className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.emissions.stat2')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{t('article.emissions.statValue2')}</div>
                <p className="text-sm text-gray-600">{t('article.emissions.statDesc2')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                  MSC Emissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">15.6M</div>
                <p className="text-sm text-gray-600">Tonnes CO2</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.emissions.surgeTitle')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.emissions.surgeContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.emissions.diversionTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.emissions.diversionContent')}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>{t('article.emissions.routeExtension').split(':')[0]}:</strong> {t('article.emissions.routeExtension').split(':')[1]}</li>
              <li><strong>{t('article.emissions.distanceIncrease').split(':')[0]}:</strong> {t('article.emissions.distanceIncrease').split(':')[1]}</li>
              <li><strong>{t('article.emissions.fleetGrowth').split(':')[0]}:</strong> {t('article.emissions.fleetGrowth').split(':')[1]}</li>
              <li><strong>{t('article.emissions.perShipEmissions').split(':')[0]}:</strong> {t('article.emissions.perShipEmissions').split(':')[1]}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.emissions.leadingTitle')}</h3>
            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.emissions.topEmissionsTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.emissions.mscEmissions').split(':')[0]}:</strong> {t('article.emissions.mscEmissions').split(':')[1]}</li>
                <li><strong>{t('article.emissions.maerskEmissions').split(':')[0]}:</strong> {t('article.emissions.maerskEmissions').split(':')[1]}</li>
                <li><strong>{t('article.emissions.cmacgmEmissions').split(':')[0]}:</strong> {t('article.emissions.cmacgmEmissions').split(':')[1]}</li>
                <li><strong>Fleet Size Factor:</strong> MSC operates the largest container fleet by vessel count</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Operational Factors</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Despite the longer routes, shipping lines maintained operational efficiency where possible. 
              Average speed remained unchanged at 13 knots, indicating that carriers prioritized 
              schedule reliability over speed optimization. This approach helped maintain service 
              quality while managing the environmental impact of extended routing.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Broader Shipping Sector Impact</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Other Vessel Categories:</h4>
              <ul className="space-y-2">
                <li><strong>Fossil Fuel Carriers:</strong> 19% of total emissions</li>
                <li><strong>Crude Oil Tankers:</strong> Emissions rose to 2019 highs</li>
                <li><strong>LNG Carriers:</strong> Decreased to 4M tonnes (from 5M in 2023)</li>
                <li><strong>Top Tanker Emitters:</strong> Cardiff Marine, Angelicoussis Group, Scorpio Group</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Environmental Policy Response</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Transport & Environment emphasized the urgent need for policy intervention:
            </p>
            <blockquote className="border-l-4 border-red-500 pl-6 italic text-gray-700 mb-6">
              "This is a wake-up call. We need policy tools that address operational behaviours, 
              reward energy efficiency measures and support the use of e-fuels. Only like this 
              will it be possible to reduce fuel consumption without slowing down global trade."
              <footer className="text-sm font-semibold mt-2">— Transport & Environment</footer>
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Sources and Methodology</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The analysis is based on Thetis-MRV data and Clarksons intelligence, providing 
              comprehensive coverage of EU shipping emissions. The Thetis-MRV system tracks 
              emissions from ships calling at EU ports, offering detailed insights into the 
              environmental impact of maritime transport operations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Policy Implications</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Recommended Policy Tools:</h4>
              <ul className="space-y-2">
                <li><strong>Operational Behavior Incentives:</strong> Policies targeting shipping efficiency</li>
                <li><strong>Energy Efficiency Rewards:</strong> Financial incentives for cleaner operations</li>
                <li><strong>E-Fuel Support:</strong> Investment in alternative fuel infrastructure</li>
                <li><strong>Emission Reduction Targets:</strong> Binding goals without trade disruption</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Future Outlook</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The 2024 emissions surge highlights the complex relationship between geopolitical 
              stability and environmental goals. As the shipping industry navigates security 
              concerns in key maritime corridors, the challenge becomes maintaining global trade 
              flows while advancing decarbonization objectives.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Response Requirements</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The record emissions levels underscore the urgent need for comprehensive industry 
              action. This includes accelerated adoption of alternative fuels, improved operational 
              efficiency measures, and coordinated policy responses that address both immediate 
              environmental concerns and long-term sustainability goals.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on Transport & Environment analysis of Thetis-MRV data reported by Kuehne+Nagel maritime news. 
                <a href="https://mykn.kuehne-nagel.com/news/article/eu-ship-emissions-hit-record-high-in-2024" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default ShipEmissionsArticle;